import axios from 'axios'
import ApiConfig from '../config'
import { v4 as uuidv4 } from 'uuid'
import { getActingAsHeader } from 'src/utils/actingAs'

const handleUnauthorized = (response) => {
  if (response?.status === 401) {
    localStorage.removeItem('accessToken')

    if (window.location.pathname !== '/secure/login') {
      window.location.href = '/secure/login'
    }
  }
}

export const postAPIHandler = async ({
  endPoint,
  data,
  id,
  headers: customHeaders = {},
}) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()
  const isFormData = data instanceof FormData

  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
    ...customHeaders,
  }

  try {
    const response = await axios({
      method: 'POST',
      url: id ? `${ApiConfig[endPoint]}/${id}` : ApiConfig[endPoint],
      headers,
      data,
      validateStatus: () => true, // keep as-is
    })

    handleUnauthorized(response)
    return response
  } catch (error) {
    handleUnauthorized(error.response)
    return {
      status: 500,
      message: error.message,
      data: null,
      headers: {},
    }
  }
}


export const getAPIHandler = async ({
  endPoint,
  id,
  source,
  paramsData,
}) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    'Authorization': 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({
      method: 'GET',
      url: id ? `${ApiConfig[endPoint]}/${id}` : ApiConfig[endPoint],
      headers,
      cancelToken: source ? source.token : null,
      params: paramsData || null,
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const deleteAPIHandler = async ({ endPoint, id, userId }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({
      method: 'DELETE',
      url: `${ApiConfig[endPoint]}/${id}`,
      headers,
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const putAPIHandler = async ({ endPoint, id, userId, data }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({
      method: 'PUT',
      url: `${ApiConfig[endPoint]}/${id}`,
      headers,
      data,
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const patchAPIHandler = async ({ endPoint, id, data }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({
      method: 'PATCH',
      url: id ? `${ApiConfig[endPoint]}/${id}` : ApiConfig[endPoint],
      headers,
      data,
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const rawAPICall = async ({ method, url }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({ method, url, headers })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const downloadTaxAPICall = async ({ method, url }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  try {
    return await axios({
      method,
      url,
      headers,
      responseType: 'blob',
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}

export const downloadAPICall = async ({ endPoint, id, paramsData = {} }) => {
  const correlationId = uuidv4()
  const requestId = uuidv4()
  const requestTime = new Date().toISOString()

  const headers = {
    'Content-Type': 'application/json',
    'X-REQUEST-DATE': requestTime,
    'X-CORRELATION-ID': correlationId,
    'X-REQUEST-ID': requestId,
    'X-CLIENT-APP-ID': 'REACT_BETA',
    ...getActingAsHeader(),
    Authorization: 'Bearer ' + window.localStorage.getItem('accessToken'),
  }

  const queryString = new URLSearchParams(paramsData).toString()
  const baseUrl = id ? `${ApiConfig[endPoint]}/${id}` : ApiConfig[endPoint]
  const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl

  try {
    return await axios({
      method: 'GET',
      url: finalUrl,
      headers,
      responseType: 'blob',
    })
  } catch (error) {
    handleUnauthorized(error.response)
    return error.response
  }
}
